import { useMemo, useState } from 'react';
import { ICountryExchangeDetail } from 'types/countries';
import { useCatalogExchange } from 'hooks/useCatalogExchange';
import { NudosButton } from 'components/NudosComponents';
import UploadForm from 'views/bulkUpload/components/UploadForm/UploadForm';
import { IProductType } from '../../DownloadUploadCatalog.types';
import { UploadFormOption, UploadFormOptionDropdown } from 'views/bulkUpload/components/UploadForm/UploadForm.types';
import { renderDropdownOptionsDownload } from '../../utils/renderOptions';
import { downloadTemplate, downloadTemplateWithoutData } from 'services/bulkUpload/uploadCatalog.service';
import { downloadTemplateInfo } from '../../content/downloadUploadCatalogInfo';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import './DownloadCatalogStep0.scss';

interface DownloadCatalogStep0Props {
  selectedCountry: ICountryExchangeDetail | undefined;
  handleSelectedCountry: (country: ICountryExchangeDetail) => void;
  nextStep: () => void;
  replaceCatalog: boolean;
  handleReplaceCatalog: (value: boolean) => void;
  disableReplaceCatalog: boolean;
}

const DownloadCatalogStep0 = ({
  selectedCountry,
  handleSelectedCountry,
  nextStep,
  replaceCatalog
}: DownloadCatalogStep0Props) => {
  const { countries } = useCatalogExchange();
  const [selectedProductType, setSelectedProductType] = useState<IProductType | undefined>();
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);

  const dropdownOptionsDownload = useMemo<UploadFormOptionDropdown[]>(
    () =>
      renderDropdownOptionsDownload(
        countries,
        selectedCountry,
        handleSelectedCountry,
        selectedProductType,
        setSelectedProductType
      ),
    [countries, handleSelectedCountry, selectedCountry, selectedProductType]
  );

  const DownloadFormOptions = useMemo<UploadFormOption[]>(
    () => [...dropdownOptionsDownload],
    [dropdownOptionsDownload]
  );

  const handleOpenDownloadedTemplate = async () => {
    if (!selectedCountry || !selectedProductType) return;
    const onlyActiveProducts: Record<number, boolean | null> = {
      1: null,
      2: true
    };
    if (selectedProductType.id === 3) {
      const template = await downloadTemplateWithoutData();
      window.open(template);
    } else {
      const template = await downloadTemplate(
        selectedCountry?.id || 0,
        selectedProductType?.id || 0,
        onlyActiveProducts[selectedProductType.id]
      );
      window.open(template?.url);
    }
  };

  const handleButtonDownloadTemplate = async () => {
    setIsDownloadingTemplate(true);
    try {
      await handleOpenDownloadedTemplate();
      nextStep();
    } catch (error) {
      displayErrorNotification({ customJSXMessage: <>Error al descargar la plantilla</> });
    }
    setIsDownloadingTemplate(false);
  };

  return (
    <UploadForm
      options={DownloadFormOptions}
      backLink="/bulk-upload/type-of-charge"
      confirmationModal={replaceCatalog}
      fileInputIsDisabled={!selectedCountry}
      uploadInfo={downloadTemplateInfo}
      showSeparatorInOptions={false}
      hideFileInput
      hideAlert
      customClassName="downloadUploadCatalogFormStep0"
      customSubmitSection={
        <NudosButton
          buttonText="Descargar plantilla y continuar"
          handleClick={handleButtonDownloadTemplate}
          isButtonDisabled={!selectedCountry || !selectedProductType}
          customWidth="263px"
          customHeight="44px"
          isButtonLoading={isDownloadingTemplate}
        />
      }
    />
  );
};

export default DownloadCatalogStep0;
